import { useState, useEffect } from "react";
import { userRequest } from "../../requestMethods";
import Loading from "../../components/loading/loading";
import { formatTime24hourFormat } from "../../util/helper";
import CustomGrid from "../../components/grid/CustomGrid";
import { useCompany } from "../../components/context/CompanyContext";

const BASE_INTEGRATION_URL = "https://integrationservice.mooda.app.br/api";

const STATUS_CARDS = [
  { key: "produtos", label: "Produtos", path: "importProdutos" },
  { key: "pedidos", label: "Pedidos", path: "importpedidos" },
  { key: "estoque", label: "Atualização de Estoque", path: "atualizaEstoqueFull" },
];

const COLORS = {
  green:  { border: "#22c55e", badge: { bg: "#dcfce7", text: "#16a34a" } },
  yellow: { border: "#f59e0b", badge: { bg: "#fef9c3", text: "#b45309" } },
  red:    { border: "#ef4444", badge: { bg: "#fee2e2", text: "#dc2626" } },
  gray:   { border: "#d1d5db", badge: { bg: "#f3f4f6", text: "#6b7280" } },
};

const BADGE_LABELS = { green: "Ok", yellow: "Atenção", red: "Crítico", gray: "Nunca" };

function getIndicator(isoDate) {
  if (!isoDate) return "gray";
  const days = (Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24);
  if (days <= 1) return "green";
  if (days <= 7) return "yellow";
  return "red";
}

function formatDateTime(iso) {
  if (!iso) return "Nunca executado";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function StatusCards({ status, companyId, externalIntegration, urlExternalIntegration, onRefreshStatus, onRefreshLogs }) {
  const [loadingKey, setLoadingKey] = useState(null);

  const handleIntegrate = async (key, path) => {
    if (loadingKey) return;
    setLoadingKey(key);
    try {
      const url = `${BASE_INTEGRATION_URL}/${companyId}/${urlExternalIntegration}/${path}`;
      await fetch(url, { method: "GET" });
      await Promise.all([onRefreshStatus(), onRefreshLogs()]);
    } catch (e) {
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
      {STATUS_CARDS.map(({ key, label, path }) => {
        const indicator = getIndicator(status[key]);
        const color = COLORS[indicator];
        const isLoading = loadingKey === key;
        return (
          <div
            key={key}
            style={{
              flex: "0 0 auto",
              width: 180,
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.08)",
              borderLeft: `4px solid ${color.border}`,
              borderRadius: 4,
              padding: "10px 12px",
              boxShadow: "0px 4px 8px 0px rgba(0,0,0,0.06)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.7px", color: "#555" }}>
                {label}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                background: color.badge.bg, color: color.badge.text, whiteSpace: "nowrap",
              }}>
                {BADGE_LABELS[indicator]}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "#999", fontWeight: 600, marginBottom: 3 }}>Última execução</div>
            <div style={{ fontSize: 13, color: "#222", fontWeight: 600, marginBottom: externalIntegration ? 12 : 0 }}>
              {formatDateTime(status[key])}
            </div>
            {externalIntegration && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  onClick={() => handleIntegrate(key, path)}
                  disabled={isLoading}
                  style={{
                    padding: "4px 16px",
                    fontSize: 11,
                    fontWeight: 700,
                    border: `1px solid ${color.border}`,
                    borderRadius: 4,
                    background: isLoading ? "#f3f4f6" : "#fff",
                    color: isLoading ? "#999" : color.badge.text,
                    cursor: isLoading ? "not-allowed" : "pointer",
                    letterSpacing: "0.5px",
                    transition: "background 0.15s",
                  }}
                >
                  {isLoading ? "Aguarde..." : "Integrar"}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function getProdutosArray(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.products)) return json.products;
  if (Array.isArray(json.produtos)) return json.produtos;
  return [json];
}

function listProdutos(json) {
  return getProdutosArray(json)
    .map((p) => (typeof p === "object" && p !== null ? p.codigo ?? "" : p))
    .filter((c) => c !== "" && c != null)
    .join(", ");
}

function countProdutos(json) {
  return getProdutosArray(json).length;
}

export default function IntegrationError() {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [logIntegracao, setLogIntegracao] = useState([]);
  const [status, setStatus] = useState({ produtos: null, pedidos: null, estoque: null });
  const { companyId, externalIntegration, urlExternalIntegration } = useCompany();

  useEffect(() => {
    if (!companyId) return;
    gtLogs();
    gtStatus();
    gtLogIntegracao();
  }, [companyId]);

  const gtLogIntegracao = async () => {
    try {
      const res = await userRequest.get(`${companyId}/logIntegration`);
      setLogIntegracao(
        res.data.map((item) => ({
          id: item.logIntegrationId,
          integration: item.integration,
          produtos: listProdutos(item.json),
          qtde: countProdutos(item.json),
          createdAt: formatTime24hourFormat(item.createdAt),
        })),
      );
    } catch (e) {}
  };

  const gtStatus = async () => {
    try {
      const res = await userRequest.get(`${companyId}/logIntegration/status`);
      setStatus(res.data);
    } catch (e) {}
  };

  const gtLogs = async () => {
    setIsLoading(true);
    try {
      const res = await userRequest.get(`${companyId}/integration/logs`);
      setData(
        res.data.map((item) => ({
          integrationId: item.logId,
          integration: item.integration,
          json: item.json ? JSON.stringify(item.json) : null,
          rtJson: item.rtJson ? JSON.stringify(item.rtJson) : null,
          createdAt: formatTime24hourFormat(item.createdAt),
        })),
      );
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { field: "integration", headerName: "Integração", width: 150, align: "left", headerAlign: "left" },
    { field: "json", headerName: "Recebido", width: 400, align: "left", headerAlign: "left" },
    { field: "rtJson", headerName: "Retorno", width: 600, align: "left", headerAlign: "left" },
    { field: "createdAt", headerName: "Criada em", width: 200, align: "center", headerAlign: "center", format: "dd/MM/yyyy HH:mm:ss" },
  ];

  const logIntegracaoColumns = [
    { field: "integration", headerName: "Integração", width: 250, align: "left", headerAlign: "left" },
    { field: "produtos", headerName: "Produtos", width: 400, align: "left", headerAlign: "left" },
    { field: "qtde", headerName: "Qtde", width: 100, align: "center", headerAlign: "center" },
    { field: "createdAt", headerName: "Criado em", width: 200, align: "center", headerAlign: "center", format: "dd/MM/yyyy HH:mm:ss" },
  ];

  return (
    <div className="cust-page">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="cust-title">Log de Integrações</div>
          <StatusCards
            status={status}
            companyId={companyId}
            externalIntegration={externalIntegration}
            urlExternalIntegration={urlExternalIntegration}
            onRefreshStatus={gtStatus}
            onRefreshLogs={gtLogs}
          />
          <div className="cust-card">
            <div className="cust-section-header">Log de Integração</div>
            <div style={{ padding: "16px" }}>
              <CustomGrid
                gridId="logintegracao"
                rows={logIntegracao}
                columns={logIntegracaoColumns}
                pageSize={45}
                checkboxSelection={false}
              />
            </div>
          </div>
          <div className="cust-card">
            <div className="cust-section-header">Registros de Erros</div>
            <div style={{ padding: "16px" }}>
              <CustomGrid
                gridId="integrationerror"
                rows={data}
                columns={columns}
                pageSize={45}
                checkboxSelection={false}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
